import { useEffect, useMemo } from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import Logo from '/assets/logo/logo_blue.svg';
import { StyledContainer, login } from './Login.style';
import { loginValidation } from '~/utils/validation';
import {
  initialLoginForm,
  LoginProp,
} from '~/components/Login/LoginForm/LoginForm.constant';
import { loginErrorMessage } from '~/utils/constants';
import { TLoginData } from '~/components/Login/Login.type';
import { useLazyGetUserTokenQuery } from '~/api/oneClub';
import {
  TOneClubGetUserTokenCode,
  TOneClubGetUserTokenData,
} from '~/api/oneClub/oneclub.type';
import { useLazyGetOwnInfoQuery } from '~/api/oneBoxV3';
import { useCommon } from '~/utils/hook/useCommon';
import { Authority } from '~/types/enum';
export const Login = () => {
  const {
    getRememberedUsername,
    setRememberedUsername,
    deleteRememberedUsername,
  } = useCommon();
  const [getUserToken] = useLazyGetUserTokenQuery();
  const [getOwnInfo] = useLazyGetOwnInfoQuery();
  const formik = useFormik({
    initialValues: initialLoginForm,
    validationSchema: loginValidation,
    onSubmit: async values => {
      await handleLogin(values);
    },
  });

  const {
    errors,
    touched,
    setErrors,
    resetForm,
    setFieldValue,
    values,
    handleChange,
    handleSubmit,
  } = formik;

  const hasInvalidInput = useMemo(
    () => ({
      username: !!(touched.username && errors.username),
      password: !!(touched.username && errors.password),
    }),
    [errors, touched],
  );

  const handleLogin = async ({
    username,
    password,
    isRememberUsername,
  }: TLoginData) => {
    const res = await getUserToken({
      username,
      password,
      from: 'Nani',
    }).unwrap();
    const { code, data, jwt } = res;
    if (code === TOneClubGetUserTokenCode.failure) {
      if (
        data === TOneClubGetUserTokenData.PASSWORD ||
        data === TOneClubGetUserTokenData.USER
      ) {
        setErrors({
          username: loginErrorMessage.wrongInput,
          password: loginErrorMessage.wrongInput,
        });
      } else {
        setErrors({
          username: loginErrorMessage.unknown,
          password: loginErrorMessage.unknown,
        });
      }
      return;
    }

    if (jwt) {
      resetForm();
      const { data: resProfile } = await getOwnInfo().unwrap();

      if (resProfile) {
        const { authority } = resProfile;
        if (authority === Authority.CLIENT || authority === Authority.TEACHER) {
          setErrors({
            username: loginErrorMessage.permissionDenied,
            password: loginErrorMessage.permissionDenied,
          });
        } else {
          if (isRememberUsername) {
            setRememberedUsername(username);
          } else {
            deleteRememberedUsername();
          }
          // 登入後導向由 AdminLayout 依 URL / state 的 redirect 統一處理，避免與表單競態而落到預設 /admin
        }
      }
    }
  };

  useEffect(() => {
    void (async () => {
      const rememberedUsername = await getRememberedUsername();
      void setFieldValue(LoginProp.username, rememberedUsername || '');
      void setFieldValue(LoginProp.isRememberUsername, !!rememberedUsername);
    })();
  }, []);

  return (
    <StyledContainer>
      <img src={Logo} alt="logo" style={{ width: '212px', margin: '0 auto' }} />
      {(errors[`${LoginProp.username}`] === loginErrorMessage.unknown ||
        errors[`${LoginProp.username}`] === loginErrorMessage.wrongInput ||
        errors[`${LoginProp.username}`] ===
          loginErrorMessage.permissionDenied) && (
        <Alert severity="error">
          {errors.username ? errors.username : errors.password}
        </Alert>
      )}
      <FormControl fullWidth variant="outlined">
        <Typography variant="subtitle1" component="label">
          帳號
        </Typography>
        <login.OutlinedInput
          id="username"
          type="username"
          value={values.username}
          onChange={handleChange}
          error={hasInvalidInput.username}
        />
      </FormControl>
      <FormControl fullWidth variant="outlined">
        <Typography variant="subtitle1" component="label">
          密碼
        </Typography>
        <login.OutlinedInput
          id="password"
          type="password"
          value={values.password}
          onChange={handleChange}
          onKeyUp={e => {
            e.key === 'Enter' && handleSubmit();
          }}
          error={hasInvalidInput.password}
        />
      </FormControl>
      <FormControlLabel
        control={
          <login.Checkbox
            name="isRememberUsername"
            checked={values.isRememberUsername}
            onChange={handleChange}
          />
        }
        label="記住我"
      />
      <login.SubmitButton
        fullWidth
        variant="contained"
        onClick={() => {
          handleSubmit();
        }}
      >
        登入
      </login.SubmitButton>
    </StyledContainer>
  );
};
